import { Mic, Users, Heart, ChevronRight, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const speakerImage = "https://customer-assets.emergentagent.com/job_choice-omw-launch/artifacts/joyj41ji_IMG_5165.jpeg";

const speakingTopics = [
  "Business & Entrepreneurship",
  "Financial Literacy & Wealth Building",
  "Money Management",
  "Overcoming Bankruptcy",
  "Women's Empowerment",
  "Singles Walking in Wholeness",
  "Leadership Development",
  "Faith & Purpose",
  "Personal Development",
];

const engagementTypes = [
  { type: "Corporate Seminars", icon: "💼" },
  { type: "Conferences & Keynotes", icon: "🎤" },
  { type: "Retreats", icon: "🏕️" },
  { type: "Church Services", icon: "⛪" },
  { type: "Women's Events (WAVE)", icon: "👑" },
  { type: "Singles Ministry", icon: "🦋" },
];

const ministryServices = [
  { service: "Mobile Wedding Officiating", icon: "💒" },
  { service: "Prayer & Counseling", icon: "🙏" },
  { service: "Notary Services", icon: "📋" },
];

const Speaking = () => {
  return (
    <section id="speaking" className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">
            <Mic className="w-3 h-3 mr-1" />
            Speaking & Ministry
          </Badge>
          
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Book LaVista for Your Next Event
          </h2>
          
          <p className="text-lg text-muted-foreground">
            Inspiring audiences with practical strategies for business, faith, and personal transformation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Image & Quick Info */}
          <div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl mb-8">
              <img 
                src={speakerImage} 
                alt="LaVista Smith Speaking" 
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-accent/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-cream font-display text-2xl font-bold">LaVista Smith</p>
                <p className="text-champagne/90">Speaker • Minister • Business Strategist</p>
              </div>
            </div>

            {/* Ministry Services */}
            <Card className="bg-accent text-accent-foreground border-0">
              <CardContent className="p-6">
                <h4 className="font-bold text-cream mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-gold" />
                  Ministry Services
                </h4>
                <div className="space-y-3">
                  {ministryServices.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-lg">{item.icon}</span>
                      <span className="text-champagne">{item.service}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right - Topics & Engagement Types */}
          <div className="space-y-8">
            {/* Speaking Topics */}
            <div>
              <h3 className="font-display text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Mic className="w-5 h-5 text-primary" />
                Speaking Topics
              </h3>
              <div className="flex flex-wrap gap-2">
                {speakingTopics.map((topic, index) => (
                  <Badge 
                    key={index}
                    variant="outline" 
                    className="bg-muted/50 text-foreground border-border px-3 py-1.5"
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Engagement Types */}
            <div>
              <h3 className="font-display text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Engagement Types
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {engagementTypes.map((eng, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-primary/10 transition-colors"
                  >
                    <span className="text-xl">{eng.icon}</span>
                    <span className="text-sm font-medium text-foreground">{eng.type}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-primary to-coral rounded-2xl p-8 text-center">
              <h4 className="font-display text-2xl font-bold text-primary-foreground mb-3">
                Ready to Inspire Your Audience?
              </h4>
              <p className="text-primary-foreground/80 mb-6">
                Let's discuss how I can bring value to your next event.
              </p>
              <Button asChild size="lg" className="bg-white text-primary hover:bg-cream rounded-full px-8">
                <a href="#booking" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Book Speaking Engagement
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Speaking;
